import {Component, DestroyRef, DoCheck, OnDestroy, OnInit} from '@angular/core';
import {NgClass} from '@angular/common';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {Subscription} from 'rxjs';
import {LookupService} from '../../common/service/lookup.service';
import {VariationType} from '../../common/models/variation-type.model';
import {VariationApproval} from '../../common/models/variation-approval.model';
import {PlanModelService} from '../../common/service/plan-model.service';
import {PermissionsService} from '../../common/service/permissions.service';
import {NlrHasPermissionsPipe} from '../../common/pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../common/pipe/nlr-permissions-disabled-pipe';
import {TraitsService} from '../../common/service/traits.service';
import {NlrFormFieldHasErrorPipe} from '../../common/pipe/nlr-form-field-has-error.pipe';
import {NlrTooltipDirective} from '../../common/directive/tooltip/nlr-tooltip-directive';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'variation-approvals',
    imports: [FormsModule, NgClass, NlrHasPermissionsPipe, NlrPermissionsDisabledPipe, NlrFormFieldHasErrorPipe, NgSelectModule, NlrTooltipDirective],
    templateUrl: './variation-approvals.component.html',
    styleUrl: './variation-approvals.component.scss',
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class VariationApprovalsComponent implements OnInit, OnDestroy, DoCheck {
    title = 'Survey method and plan variation approvals';

    permissions = ['EDIT_CERTIFICATION', 'VIEW_CERTIFICATION'];

    variationTypes: VariationType[] = [];

    allVariationTypes: VariationType[] = [];

    traitsChangedSubscription: Subscription;

    constructor(
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private permissionsService: PermissionsService,
        private traitsService: TraitsService,
        private destroyRef: DestroyRef
    ) {}

    ngOnInit() {
        this.allVariationTypes = this.lookupService.getLookups('variationType');

        this.traitsChangedSubscription = this.eventBus
            .on('nlr:trait-service:traitsChanged')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.log(`${this.title} - nlr:trait-service:traitsChanged`, meta.data);

                this.variationTypes = this.allVariationTypes.filter(
                    variationType => variationType.active && this.traitsService.criteriaMatches(variationType.planTraitCriteria, true)
                );

                const variationTypeIds = this.variationTypes.map(variationType => variationType.id);

                const variationApprovals = this.getVariationApprovals();

                // make sure that any existing variation types that are no longer valid are removed
                variationApprovals.splice(
                    0,
                    variationApprovals.length,
                    ...variationApprovals.filter(approval => !approval.variationType || variationTypeIds.includes(approval.variationType.id))
                );

                // add a new blank row if all rows are deleted
                if (variationApprovals.length === 0) {
                    this.addVariationApproval();
                }
            });
    }

    ngOnDestroy() {
        this.traitsChangedSubscription.unsubscribe();
    }

    ngDoCheck(): void {
        // Set variationApproval#variationType to corresponding item in this.allVariationTypes, so it gets selected after save / reload
        this.getVariationApprovals().forEach(variationApproval => {
            if (variationApproval.variationType) {
                const variationTypeLookup = this.allVariationTypes.find(variationType => variationType.id === variationApproval.variationType.id);
                if (variationApproval.variationType !== variationTypeLookup) {
                    variationApproval.variationType = variationTypeLookup;
                }
            } else {
                // Ensure empty variationType set to null (not undefined or "") as the variation type select element's default option is bound to null
                variationApproval.variationType = null;
            }
        });
    }

    showComponent() {
        return this.permissionsService.hasPermission(this.permissions);
    }

    addVariationApproval() {
        console.debug(`${this.title} - addVariationApproval`);

        this.getVariationApprovals().push({
            variationType: null,
            regulatoryStatement: '',
            approvalReference: ''
        });
    }

    isAddVariationApprovalDisabled() {
        return !this.hasEditPermission();
    }

    removeVariationApproval(index: number) {
        console.debug(`${this.title} - removeVariationApproval - index=${index}`);

        const variationApprovals = this.getVariationApprovals();

        if (index === 0 && variationApprovals.length === 1) {
            Object.assign(variationApprovals[index], {variationType: null, regulatoryStatement: '', approvalReference: ''});
        } else {
            variationApprovals.splice(index, 1);
        }
    }

    isRemoveVariationApprovalDisabled() {
        return !this.hasEditPermission();
    }

    variationTypeTrackBy(variationType: VariationType) {
        return variationType?.id;
    }

    variationTypeChange(variationApproval: VariationApproval, variationType: VariationType) {
        variationApproval.variationType = variationType;
        variationApproval.regulatoryStatement = variationApproval.variationType?.defaultStatutoryStatement || '';
        variationApproval.approvalReference = '';
    }

    variationTypeCloseTooltip(component) {
        this.eventBus.cast(`${component}:tooltipClose`);
    }

    isVariationTypeDisabled() {
        return !this.hasEditPermission();
    }

    isApprovalReferenceDisabled(variationApproval: VariationApproval) {
        return !this.hasEditPermission() || !variationApproval.variationType?.enableApprovalReference;
    }

    isRegulatoryStatementDisabled(variationApproval: VariationApproval) {
        return !this.hasEditPermission() || !variationApproval.variationType?.enableStatutoryStatement;
    }

    getVariationApprovals() {
        return this.getCertification().variationApprovals || [];
    }

    private getCertification() {
        return this.planModelService.getModel().certification;
    }

    private hasEditPermission() {
        return !this.planModelService.readonly && this.permissionsService.hasEditPermission(this.permissions);
    }
}
